/** @jsxImportSource ./simboluri-jsx */
import { useEffect, useState } from "react";
import { Prefs, Reglaje, SetariExecutie } from "./tipuri";
import { getPrefs, getReglaje, savePrefs, stergeReglaje } from "./apiStructura";
import { BandaDoarCitire, useRolProiect } from "../components/rol";
import { t, tn, tx, useT } from "../i18n";
import type { Cheie } from "../i18n";

/* Setările de calcul ale structurii.
 *
 * Pagina avea 91 de câmpuri în două taburi. Are 6, după auditul din
 * Din 157 de chei de preferință, 49 au fost RETRASE, iar
 * restul nu erau decizii de om — erau fapte ale proiectului, decizii de
 * solver sau doctrina de calcul. Regula care le-a separat:
 *
 *   o SETARE e ceva ce dictează atelierul de fasonat sau șantierul.
 *
 * Calculul n-are de unde s-o afle, iar optimizarea (azi pe scor, mâine și pe
 * cost) n-o poate alege în locul omului: dacă biroul îndoaie barele, le
 * îndoaie fiindcă are mașina și practica, nu fiindcă iese cu 2 % mai ieftin.
 * Tot ce a rămas aici are, în plus, TOATE variantele implementate — o
 * opțiune care arată o alegere inexistentă e mai rea decât lipsa ei.
 *
 * Nici marca de oțel nu mai e aici: «nu e un user
 * config, calculăm noi cum e mai bine» — azi BSt500, mâine ce alege
 * optimizarea de cost, ca la clasa de beton.
 *
 * Lista se extinde UNA CÂTE UNA, când apare nevoia, și numai cu ambele
 * variante implementate și testate. Catalogul din spate (`app.DEFAULT_PREFS`)
 * spune, pe secțiuni, unde a plecat fiecare cheie retrasă.
 */

type CampNumeric = {
  fel: "numar";
  key: keyof SetariExecutie;
  label: string;
  unit: string;
  step: number;
  hint: string;
};
type CampAlegere = {
  fel: "alegere";
  key: keyof SetariExecutie;
  label: string;
  hint: string;
  optiuni: { value: string; label: string }[];
};
/** O LISTĂ de nume, scrisă separat prin virgulă. Serverul acceptă și textul,
 *  și lista (`app.valideaza_prefs`), deci câmpul trimite ce a scris omul. */
type CampLista = {
  fel: "lista";
  key: keyof SetariExecutie;
  label: string;
  placeholder: string;
  hint: string;
};
type Camp = CampNumeric | CampAlegere | CampLista;

/** Câmpurile, cu textele în limba de ACUM: tabelul era o constantă de
 *  modul, construită o dată la încărcare, deci ar fi înghețat limba. */
const campuri = (): Camp[] => [
  {
    fel: "alegere",
    key: "mod_intrerupere",
    label: t("struct.pref.mod_intrerupere.eticheta"),
    hint: t("struct.pref.mod_intrerupere.ajutor"),
    optiuni: [
      { value: "indoite", label: t("struct.pref.mod_intrerupere.opt_indoite") },
      { value: "intrerupte", label: t("struct.pref.mod_intrerupere.opt_intrerupte") },
    ],
  },
  {
    fel: "alegere",
    key: "etrieri_pas_unic",
    label: t("struct.pref.etrieri_pas_unic.eticheta"),
    hint: t("struct.pref.etrieri_pas_unic.ajutor"),
    optiuni: [
      { value: "1", label: t("struct.pref.etrieri_pas_unic.opt_1") },
      { value: "0", label: t("struct.pref.etrieri_pas_unic.opt_0") },
    ],
  },
  {
    fel: "alegere",
    key: "allow_odd_bars",
    label: t("struct.pref.allow_odd_bars.eticheta"),
    hint: t("struct.pref.allow_odd_bars.ajutor"),
    optiuni: [
      { value: "0", label: t("struct.pref.allow_odd_bars.opt_0") },
      { value: "1", label: t("struct.pref.allow_odd_bars.opt_1") },
    ],
  },
  {
    fel: "numar",
    key: "interax_min_cm",
    label: t("struct.pref.interax_min_cm.eticheta"),
    unit: "cm",
    step: 0.5,
    hint: t("struct.pref.interax_min_cm.ajutor"),
  },
  {
    fel: "numar",
    key: "s_max_bare_cm",
    label: t("struct.pref.s_max_bare_cm.eticheta"),
    unit: "cm",
    step: 1,
    hint: t("struct.pref.s_max_bare_cm.ajutor"),
  },
  {
    fel: "lista",
    key: "niveluri_plan_liber",
    label: t("struct.pref.niveluri_plan_liber.eticheta"),
    placeholder: t("struct.pref.niveluri_plan_liber.exemplu"),
    hint: t("struct.pref.niveluri_plan_liber.ajutor"),
  },
  {
    fel: "alegere",
    key: "infrastructura_libera",
    label: t("struct.pref.infrastructura_libera.eticheta"),
    hint: t("struct.pref.infrastructura_libera.ajutor"),
    optiuni: [
      { value: "auto", label: t("struct.pref.infrastructura_libera.opt_auto") },
      { value: "da", label: t("struct.pref.infrastructura_libera.opt_da") },
      { value: "nu", label: t("struct.pref.infrastructura_libera.opt_nu") },
    ],
  },
  {
    fel: "numar",
    key: "modul_grila_m",
    label: t("struct.pref.modul_grila_m.eticheta"),
    unit: "m",
    step: 0.1,
    hint: t("struct.pref.modul_grila_m.ajutor"),
  },
  {
    fel: "lista",
    key: "distante_ideale_m",
    label: t("struct.pref.distante_ideale_m.eticheta"),
    placeholder: t("struct.pref.distante_ideale_m.exemplu"),
    hint: t("struct.pref.distante_ideale_m.ajutor"),
  },
  {
    fel: "numar",
    key: "h_grinda_max_m",
    label: t("struct.pref.h_grinda_max_m.eticheta"),
    unit: "m",
    step: 0.05,
    hint: t("struct.pref.h_grinda_max_m.ajutor"),
  },
  {
    fel: "numar",
    key: "h_grinda_fatada_max_m",
    label: t("struct.pref.h_grinda_fatada_max_m.eticheta"),
    unit: "m",
    step: 0.05,
    hint: t("struct.pref.h_grinda_fatada_max_m.ajutor"),
  },
  {
    fel: "alegere",
    key: "drift_pe_baza",
    label: t("struct.pref.drift_pe_baza.eticheta"),
    hint: t("struct.pref.drift_pe_baza.ajutor"),
    optiuni: [
      { value: "auto", label: t("struct.pref.drift_pe_baza.opt_auto") },
      { value: "incastrat", label: t("struct.pref.drift_pe_baza.opt_incastrat") },
      { value: "elastic", label: t("struct.pref.drift_pe_baza.opt_elastic") },
    ],
  },
  {
    fel: "numar",
    key: "h_radier_m",
    label: t("struct.pref.h_radier_m.eticheta"),
    unit: "m",
    step: 0.05,
    hint: t("struct.pref.h_radier_m.ajutor"),
  },
  {
    fel: "numar",
    key: "phi_min_long_mm",
    label: t("struct.pref.phi_min_long_mm.eticheta"),
    unit: "mm",
    step: 2,
    hint: t("struct.pref.phi_min_long_mm.ajutor"),
  },
];

/** Eticheta omenească a unei chei, pentru tabelul de reglaje. Reglajele
 *  poartă chei din TOT catalogul, nu doar din setările de mai sus (solverul
 *  scrie lățimi de fundație, grosimi de placă, plafoane de grindă), deci
 *  dicționarul e separat de formular. */
const ETICHETE: Record<string, Cheie> = {
  latime_minima_m: "struct.pref.et.latime_minima",
  adancime_minima_m: "struct.pref.et.adancime_minima",
  h_grinda_fundare_m: "struct.pref.et.h_grinda_fundare",
  beton: "struct.pref.et.beton",
  h_grinda_max_m: "struct.pref.et.plafon_h_grinda",
  h_grinda_fatada_max_m: "struct.pref.et.plafon_h_grinda_fatada",
  drift_pe_baza: "struct.pref.drift_pe_baza.eticheta",
  h_radier_m: "struct.pref.h_radier_m.eticheta",
  grosime_placa_m: "struct.pref.et.grosime_placa",
  grosime_placa_max_m: "struct.pref.et.plafon_grosime_placa",
  rigidizare_directie: "struct.pref.et.rigidizare_directie",
  grosime_perete_subsol_m: "struct.pref.et.grosime_perete_subsol",
  grosime_perete_subsol_contur_m: "struct.pref.et.grosime_perete_subsol_contur",
  coef_excentricitate_pe_ax: "struct.pref.et.coef_excentricitate",
  h_mesh_m: "struct.pref.et.h_mesh",
  h_mesh_baza_m: "struct.pref.et.h_mesh_baza",
  solver_memorie: "struct.pref.et.solver_memorie",
  amprente_incercate: "struct.pref.et.amprente_incercate",
};

function eticheta(cheie: string): string {
  const c = campuri().find((f) => f.key === cheie);
  return c ? c.label : ETICHETE[cheie] ? t(ETICHETE[cheie]) : cheie;
}

/** Un reglaj poate fi și STRUCTURĂ, nu doar un număr: `solver_memorie` e un
 *  dicționar cu istoricul solverului. `String(obiect)` scria „[object
 *  Object]" — cifra pe care omul o caută nu se vedea nicăieri  *  . Le arătăm ca JSON compact, cu tot conținutul în `title`. */
function valoareCitibila(v: unknown) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") {
    const intreg = JSON.stringify(v, null, 1);
    const scurt = JSON.stringify(v);
    const n = Array.isArray(v)
      ? tn("struct.pref.intrari#n", v.length)
      : tn("struct.pref.chei#n", Object.keys(v as object).length);
    return (
      <span className="sg-mono" title={intreg} style={{ cursor: "help" }}>
        {scurt.length <= 48 ? scurt : `{…} ${n}`}
      </span>
    );
  }
  return String(v);
}

export default function Preferinte({ projectId }: { projectId: string }) {
  useT();
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Setările sunt INTRĂRI ale lanțului, deci cer drept de scriere.
  const { potScrie } = useRolProiect();
  const [reglaje, setReglaje] = useState<Reglaje | null>(null);

  useEffect(() => {
    getPrefs(projectId).then(setPrefs).catch((e) => setError(e.message));
    getReglaje(projectId).then(setReglaje).catch(() => setReglaje(null));
  }, [projectId]);

  if (error) return <div className="sg-error">{error}</div>;
  if (!prefs) return <div className="sg-muted">{t("struct.pref.incarca")}</div>;

  /** Se trimit DOAR setările pe care le stăpânește formularul.
   *
   *  Nu tot obiectul, cum se făcea până acum: o salvare care rescria toate
   *  cele 157 de chei le transforma pe toate în „valori puse de om", iar
   *  schimbarea unui implicit din cod nu mai ajungea niciodată la proiectele
   * existente (v. accidentul cu modelul rămas tăcut pe fisurat).
   *  Endpointul face merge peste rândul existent, deci trimiterea parțială e
   *  și corectă, și suficientă. */
  async function onSave() {
    if (!prefs || busy) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const doar: Partial<Prefs> = {};
      for (const c of campuri()) {
        (doar as Record<string, unknown>)[c.key] =
          (prefs as unknown as Record<string, unknown>)[c.key];
      }
      await savePrefs(projectId, doar);
      setPrefs(await getPrefs(projectId));
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function seteaza(key: keyof SetariExecutie, v: string | number) {
    setSaved(false);
    setPrefs({ ...(prefs as Prefs), [key]: v });
  }

  return (
    <div className="pj-step-page">
      <h2>{t("struct.pref.setari_calcul")}</h2>
      <BandaDoarCitire />
      <fieldset
        disabled={!potScrie}
        style={{
          border: "none", padding: 0, margin: 0, minWidth: 0,
          display: "contents",
        }}
      >
        <div className="sg-muted" style={{ marginBottom: 12, maxWidth: 820 }}>
          {tx("struct.pref.intro")}
        </div>
        {reglaje && Object.keys(reglaje.reglaje).length > 0 && (
          <div className="st-findings" style={{ marginBottom: 16 }}>
            <div className="pf-row" style={{ display: "block" }}>
              <strong>{t("struct.pref.reglaje_optimizare")}</strong>
              <div className="sg-muted" style={{ marginBottom: 8 }}>
                {t("struct.pref.valori_alese_cautare")}
              </div>
              <table className="sg-table">
                <thead>
                  <tr>
                    <th>{t("struct.dos.col_parametru")}</th>
                    <th>{t("struct.pref.valoarea_ta")}</th>
                    <th>{t("struct.pref.reglat")}</th>
                    <th>{t("struct.pref.de_cine")}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(reglaje.reglaje).map(([cheie, val]) => {
                    const j = [...reglaje.jurnal].reverse()
                      .find((x) => x.cheie === cheie);
                    const om = (reglaje.preferinte as unknown as
                      Record<string, unknown>)[cheie];
                    return (
                      <tr key={cheie}>
                        <td>{eticheta(cheie)}</td>
                        <td className="sg-muted">{valoareCitibila(om)}</td>
                        <td><strong>{valoareCitibila(val)}</strong></td>
                        <td className="sg-muted">{j?.sursa ?? t("struct.pref.flux_automat")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <button
                className="sg-btn"
                style={{ marginTop: 8 }}
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await stergeReglaje(projectId);
                    setReglaje(await getReglaje(projectId));
                  } catch (e) {
                    setError((e as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {t("struct.pref.arunca_reglajele")}
              </button>
            </div>
          </div>
        )}
        <div className="st-findings">
          <h3 style={{ marginTop: 0 }}>{t("struct.pref.executia_armaturii")}</h3>
          <div className="sg-muted" style={{ marginBottom: 8, maxWidth: 820 }}>
            {tx("struct.pref.doar_strange")}
          </div>
          {campuri().map((c) => (
            <div className="pf-row" key={c.key}>
              <label htmlFor={`pf-${c.key}`}>
                <strong>{c.label}</strong>
                <div className="sg-muted">{c.hint}</div>
              </label>
              <div className="pf-input">
                {c.fel === "numar" ? (
                  <>
                    <input
                      id={`pf-${c.key}`}
                      type="number"
                      step={c.step}
                      value={String(
                        (prefs as unknown as Record<string, unknown>)[c.key]
                          ?? "")}
                      onChange={(e) => seteaza(c.key, Number(e.target.value))}
                    />
                    <span className="sg-muted">{c.unit}</span>
                  </>
                ) : c.fel === "lista" ? (
                  <input
                    id={`pf-${c.key}`}
                    type="text"
                    placeholder={c.placeholder}
                    value={(() => {
                      const v = (prefs as unknown as
                        Record<string, unknown>)[c.key];
                      return Array.isArray(v) ? v.join(", ") : String(v ?? "");
                    })()}
                    onChange={(e) => seteaza(c.key, e.target.value)}
                  />
                ) : (
                  <select
                    id={`pf-${c.key}`}
                    value={String(
                      (prefs as unknown as Record<string, unknown>)[c.key]
                        ?? c.optiuni[0].value)}
                    onChange={(e) => {
                      const v = e.target.value;
                      // cele 0/1 sunt numere în catalog, nu texte
                      seteaza(c.key, /^[01]$/.test(v) ? Number(v) : v);
                    }}
                  >
                    {c.optiuni.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          ))}
          <div className="pf-actions">
            <button className="sg-primary" onClick={onSave} disabled={busy}>
              {busy ? t("comun.se_salveaza") : t("comun.salveaza")}
            </button>
            {saved && <span className="pf-saved">{t("struct.ret.salvat")}</span>}
          </div>
        </div>
      </fieldset>
    </div>
  );
}
